import type { Enduser } from "@tellescope/types-models";
import type { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { Label, Input } from "@tellescope/design-system";
import { Button, Progress } from "@tellescope/ui";
import { cn } from "@tellescope/utils";
import { FieldDescription } from "../../FieldDescription";
import type { TellescopeFormFieldBaseProps, TellescopeFieldResponse } from "../../types";
import { usePharmacySearchField } from "./usePharmacySearchField";
import { PharmacyCard } from "./PharmacyCard";

export interface PharmacySearchFieldProps extends TellescopeFormFieldBaseProps<"Pharmacy Search"> {
  enduser?: TellescopeModelFromName<"endusers"> | Enduser;
  formResponses: TellescopeFieldResponse[];
}

export const PharmacySearchField = ({
  title,
  description,
  htmlDescription,
  required,
  value,
  onChange,
  enduser,
  formResponses,
  className,
  error,
  autoFocus,
}: PharmacySearchFieldProps) => {
  const { t } = useTranslation("forms");
  const { zipCode, setZipCode, canSearch, search, isLoading, searchError, results, selected, select, clear } =
    usePharmacySearchField({ value, onChange, enduser, formResponses });

  return (
    <div className={cn("space-y-2", className)}>
      {!!title && <Label required={required}>{title}</Label>}
      <FieldDescription description={description} htmlDescription={htmlDescription} />

      {selected ? (
        <PharmacyCard pharmacy={selected} selected onClear={clear} />
      ) : (
        <>
          <div className="flex gap-2">
            <Input
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                search();
              }}
              data-consumes-enter-key={true}
              placeholder={t("ZIP code")}
              inputMode="numeric"
              maxLength={5}
              className="w-32"
              autoFocus={autoFocus}
            />
            <Button type="button" onClick={search} disabled={!canSearch || isLoading} variant="outline">
              {isLoading ? t("Searching...") : t("Search")}
            </Button>
          </div>

          {isLoading && <Progress value={undefined} className="h-1" />}

          {!!searchError && <p className="text-sm text-destructive">{searchError}</p>}

          {!searchError && results.length > 0 && (
            <div className="flex flex-col">
              <p className="text-xs text-muted-foreground mb-1">
                {t("{{count}} pharmacies found", { count: results.length })}
              </p>
              <div className="overflow-y-auto flex-grow flex flex-col gap-1">
                {results.map((pharmacy, index) => (
                  <PharmacyCard
                    key={`${pharmacy.ncpdpId}-${index}`}
                    pharmacy={pharmacy}
                    onClick={() => select(pharmacy)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
